/** ToDo 노트 종류(보드) — 기기 localStorage에 마지막 선택 기억
 *
 * 과거 부트스트랩은 sort 후 rows[0](항상 「기본」)을 골랐음.
 * 지금은 명시적 선택만 저장하고, 재진입 시 이 값을 우선 복원함.
 */

import { todoShareNameFromTitle } from './todoShareTitle';

export type TodoNoteSelectedBoardPreference = {
  id: string;
  name: string;
};

function cacheScopeKey(noteWorkspaceId: string | null): string {
  return noteWorkspaceId ?? 'account';
}

function storageKey(noteWorkspaceId: string | null): string {
  return `cp.todoNote.selectedBoard.v2.${cacheScopeKey(noteWorkspaceId)}`;
}

function sessionKey(noteWorkspaceId: string | null): string {
  return `cp.todoNote.selectedBoard.session.v2.${cacheScopeKey(noteWorkspaceId)}`;
}

/** v1: boardId 문자열만 저장 */
function legacyV1Key(noteWorkspaceId: string | null): string {
  return `cp.todoNote.selectedBoard.v1.${cacheScopeKey(noteWorkspaceId)}`;
}

function legacyV1UserKey(userId: string, noteWorkspaceId: string | null): string {
  return `cp.todoNote.selectedBoard.v1.${userId}.${cacheScopeKey(noteWorkspaceId)}`;
}

function parsePreference(raw: string | null): TodoNoteSelectedBoardPreference | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed) as Partial<TodoNoteSelectedBoardPreference>;
      const id = typeof parsed.id === 'string' ? parsed.id.trim() : '';
      const name = typeof parsed.name === 'string' ? parsed.name.trim() : '';
      if (!id && !name) return null;
      return { id, name };
    } catch {
      return null;
    }
  }
  return { id: trimmed, name: '' };
}

function readFromStore(
  store: Storage | undefined,
  key: string
): TodoNoteSelectedBoardPreference | null {
  if (!store) return null;
  try {
    return parsePreference(store.getItem(key));
  } catch {
    return null;
  }
}

function writeToStore(store: Storage | undefined, key: string, pref: TodoNoteSelectedBoardPreference): void {
  if (!store) return;
  try {
    store.setItem(key, JSON.stringify({ id: pref.id, name: pref.name }));
  } catch {
    /* quota / private mode */
  }
}

export function loadTodoNoteSelectedBoardPreference(
  noteWorkspaceId: string | null,
  userId?: string
): TodoNoteSelectedBoardPreference | null {
  if (typeof window === 'undefined') return null;

  const fromLocal = readFromStore(window.localStorage, storageKey(noteWorkspaceId));
  if (fromLocal) return fromLocal;

  const fromSession = readFromStore(window.sessionStorage, sessionKey(noteWorkspaceId));
  if (fromSession) {
    saveTodoNoteSelectedBoardPreference(noteWorkspaceId, fromSession);
    return fromSession;
  }

  const v1 = readFromStore(window.localStorage, legacyV1Key(noteWorkspaceId));
  if (v1) {
    saveTodoNoteSelectedBoardPreference(noteWorkspaceId, v1);
    return v1;
  }

  if (userId) {
    const legacy = readFromStore(window.localStorage, legacyV1UserKey(userId, noteWorkspaceId));
    if (legacy) {
      saveTodoNoteSelectedBoardPreference(noteWorkspaceId, legacy);
      return legacy;
    }
  }
  return null;
}

export function saveTodoNoteSelectedBoardPreference(
  noteWorkspaceId: string | null,
  pref: TodoNoteSelectedBoardPreference
): void {
  if (typeof window === 'undefined') return;
  const id = pref.id.trim();
  const name = pref.name.trim();
  if (!id && !name) return;
  const next = { id, name };
  writeToStore(window.localStorage, storageKey(noteWorkspaceId), next);
  writeToStore(window.sessionStorage, sessionKey(noteWorkspaceId), next);
}

/** 목록에서 저장된 선호을 찾아 보드 id 반환. 이름 우선(보드 재생성 시 id 변경 대비). */
export function resolveTodoNoteSelectedBoardId<T extends { id: string; title?: string | null }>(
  boards: T[],
  pref: TodoNoteSelectedBoardPreference | null
): string | null {
  if (!boards.length) return null;
  if (!pref) return boards[0]?.id ?? null;

  const prefName = pref.name.trim();
  if (prefName) {
    const byName = boards.find((b) => todoShareNameFromTitle(b.title ?? '') === prefName);
    if (byName) return byName.id;
  }

  const prefId = pref.id.trim();
  if (prefId) {
    const byId = boards.find((b) => b.id === prefId || b.id.toLowerCase() === prefId.toLowerCase());
    if (byId) return byId.id;
  }

  // 저장된 보드를 못 찾으면(삭제 등) 목록 첫 항목(정렬상 「기본」)
  return boards[0]?.id ?? null;
}
